import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField, 
  Button, 
  MenuItem, 
  Box,
  Alert
} from '@mui/material';

interface AddFindingFormProps {
  controlId: string;
  controlName: string;
  open: boolean;
  onClose: () => void;
  onSave: (finding: any) => Promise<void>;
}

export default function AddFindingForm({ 
  controlId, 
  controlName,
  open, 
  onClose, 
  onSave 
}: AddFindingFormProps) {
  const [finding, setFinding] = useState({
    evidence: '',
    remediationPlan: '',
    status: 'Open',
    priority: 'Medium',
    currentState: 'NonCompliant',
    targetDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!finding.evidence.trim()) {
      setError('Evidence is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSave({
        controlId,
        ...finding,
        targetDate: finding.targetDate || undefined
      });
      
      // Reset form
      setFinding({
        evidence: '',
        remediationPlan: '',
        status: 'Open',
        priority: 'Medium',
        currentState: 'NonCompliant',
        targetDate: ''
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create finding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Finding for {controlName}</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Evidence"
          fullWidth
          required
          multiline
          rows={4}
          sx={{ mb: 3 }}
          value={finding.evidence}
          onChange={(e) => setFinding({...finding, evidence: e.target.value})}
          placeholder="Describe the gap or non-compliance..."
        />

        <TextField
          label="Remediation Plan"
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 3 }}
          value={finding.remediationPlan}
          onChange={(e) => setFinding({...finding, remediationPlan: e.target.value})}
          placeholder="Describe how this will be remediated..."
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            select
            label="Current State"
            fullWidth
            value={finding.currentState}
            onChange={(e) => setFinding({...finding, currentState: e.target.value})}
          >
            <MenuItem value="Compliant">Compliant</MenuItem>
            <MenuItem value="Partial">Partial</MenuItem>
            <MenuItem value="NonCompliant">Non-Compliant</MenuItem>
            <MenuItem value="NotApplicable">Not Applicable</MenuItem>
          </TextField>

          <TextField
            select
            label="Status"
            fullWidth
            value={finding.status}
            onChange={(e) => setFinding({...finding, status: e.target.value})}
          >
            <MenuItem value="Open">Open</MenuItem>
            <MenuItem value="InProgress">In Progress</MenuItem>
            <MenuItem value="Resolved">Resolved</MenuItem>
            <MenuItem value="Accepted">Accepted</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            select
            label="Priority"
            fullWidth
            value={finding.priority}
            onChange={(e) => setFinding({...finding, priority: e.target.value})}
          >
            <MenuItem value="Critical">Critical</MenuItem>
            <MenuItem value="High">High</MenuItem>
            <MenuItem value="Medium">Medium</MenuItem>
            <MenuItem value="Low">Low</MenuItem>
          </TextField>

          <TextField
            label="Target Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={finding.targetDate}
            onChange={(e) => setFinding({...finding, targetDate: e.target.value})}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || !finding.evidence.trim()}
        >
          {loading ? 'Saving...' : 'Save Finding'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
